import React, { useState } from 'react';
import { Navigate, Outlet, NavLink, useLocation } from 'react-router-dom';
import { version } from '../../package.json';
import { LayoutDashboard, ShoppingBag, LayoutGrid, Menu, Store, ClipboardList, Plus, MessageSquare } from 'lucide-react';
import { useAppSelector } from '../store/hooks';
import { Sidebar } from './Sidebar';

const BASE_NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/products', label: 'Productos', icon: ShoppingBag, end: false },
  { to: '/orders', label: 'Pedidos', icon: MessageSquare, end: false },
  { to: '/categories', label: 'Categorías', icon: LayoutGrid, end: false },
];

const SUPER_NAV = [
  { to: '/logs', label: 'Actividad', icon: ClipboardList, end: false },
];

export const Layout: React.FC = () => {
  const token = useAppSelector((s) => s.auth.token);
  const user = useAppSelector((s) => s.auth.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const bottomNavItems = user?.role === 'superadmin' ? SUPER_NAV : BASE_NAV;

  if (!token) return <Navigate to="/login" replace />;

  return (
    <div className="flex h-screen bg-page-bg overflow-hidden">
      <Sidebar mobileOpen={drawerOpen} onMobileClose={() => setDrawerOpen(false)} />

      <div className="flex-1 flex flex-col lg:ml-64 h-screen min-w-0 overflow-hidden">

        {/* ── Mobile top bar ── */}
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white/95 backdrop-blur-sm border-b border-page-border">
          <button
            onClick={() => setDrawerOpen(true)}
            className="p-2 text-ink-muted hover:text-ink hover:bg-page-surface rounded-xl transition-colors"
            aria-label="Abrir menú"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="bg-brand-primary p-1.5 rounded-lg">
              <Store className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-ink text-base">Aye Otero</span>
            <span className="text-[9px] text-ink-faint font-medium bg-page-surface border border-page-border px-1 py-0.5 rounded">v{version}</span>
          </div>
        </header>

        {/* ── Main content ── */}
        <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8 overflow-y-auto bg-page-bg">
          <Outlet />
        </main>

        {/* ── Mobile bottom nav ── */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-page-border flex items-stretch shadow-[0_-1px_8px_rgba(0,0,0,0.06)]">
          {/* Left items */}
          {bottomNavItems.slice(0, 2).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => {
                  const active = isActive && !(item.to === '/products' && location.pathname === '/products/new');
                  return `flex-1 flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-semibold transition-colors relative ${active ? 'text-brand-primary' : 'text-ink-faint hover:text-ink-muted'
                    }`;
                }}
              >
                {({ isActive }) => {
                  const active = isActive && !(item.to === '/products' && location.pathname === '/products/new');
                  return (
                    <>
                      {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-brand-primary rounded-full" />}
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </>
                  );
                }}
              </NavLink>
            );
          })}

          {/* Center FAB — Nuevo Producto (solo admin) */}
          {user?.role !== 'superadmin' && (
            <div className="flex-1 flex items-center justify-center">
              <NavLink
                to="/products/new"
                className={({ isActive }) =>
                  `w-12 h-12 rounded-full flex items-center justify-center transition-all -mt-4 active:scale-95 ${isActive
                    ? 'bg-brand-primary shadow-lg shadow-brand-glow ring-4 ring-brand-primary/30 scale-105 text-white'
                    : 'bg-brand-primary hover:bg-brand-hover shadow-md text-white'
                  }`
                }
                aria-label="Nuevo producto"
              >
                <Plus className="w-5 h-5 text-white" />
              </NavLink>
            </div>
          )}

          {/* Right items */}
          {bottomNavItems.slice(2).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-semibold transition-colors relative ${isActive ? 'text-brand-primary' : 'text-ink-faint hover:text-ink-muted'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-brand-primary rounded-full" />}
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
